"use client";

import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { ShopItemFormDialog } from "./shop-item-form-dialog";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { toast } from "sonner";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { STATUS_IDS } from "@/lib/constants/statuses";

interface ShopItem {
  id: number;
  venue_id: number;
  name: string;
  category_id: number;
  pricing_id?: number;
  image_url: string | null;
  is_active: boolean;
  status_id: number;
  article_code?: string | null;
  pricing: {
    id: number;
    price: number;
    vat_percentage: number;
    currency: string;
  } | null;
  category: {
    id: number;
    name: string;
  } | null;
  created_at: string;
  updated_at: string;
}

interface ShopItemListProps {
  venueId: number;
  venueCurrency: string;
}

export function ShopItemList({ venueId, venueCurrency }: ShopItemListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [shopItems, setShopItems] = useState<ShopItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedShopItem, setSelectedShopItem] = useState<ShopItem | null>(
    null,
  );
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shopItemToDelete, setShopItemToDelete] = useState<ShopItem | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Remove auth restrictions - all users can modify shop items
  const canModify = true;

  useEffect(() => {
    fetchShopItems();
  }, [venueId]);

  const fetchShopItems = async () => {
    try {
      console.log("🔍 Fetching shop items for venueId:", venueId);
      const response = await fetch(
        `/api/venues/${venueId}/shop-items?includeArchived=true`,
      );
      if (!response.ok) throw new Error("Failed to fetch shop items");
      const data = await response.json();
      console.log("📦 Shop items API response:", data);
      console.log("🏷️ First item category:", data[0]?.category);
      const normalized = (data || []).map((item: any) => ({
        ...item,
        is_active:
          typeof item.is_active === "boolean"
            ? item.is_active
            : item.status_id === STATUS_IDS.SHOP_ITEM_ACTIVE,
      }));
      setShopItems(normalized);
    } catch (error) {
      console.error("Error fetching shop items:", error);
      toast.error("Failed to load shop items");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!shopItemToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shop-items/${shopItemToDelete.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete shop item");
      }

      toast.success("Shop item deleted successfully");

      await fetchShopItems();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete shop item");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setShopItemToDelete(null);
    }
  };

  const handleEdit = (shopItem: ShopItem) => {
    // Navigate to dedicated edit page instead of opening modal
    const currentPortal = pathname.startsWith("/booking")
      ? "booking"
      : "superadmin";
    router.push(`/${currentPortal}/venue/${venueId}/shop-items/${shopItem.id}`);
  };

  const handleCreate = () => {
    setSelectedShopItem(null);
    setShowFormDialog(true);
  };

  const handleFormSuccess = (createdItem?: ShopItem) => {
    setShowFormDialog(false);
    setSelectedShopItem(null);

    // If a new item was created, redirect to its edit page
    if (createdItem) {
      const currentPortal = pathname.startsWith("/booking")
        ? "booking"
        : "superadmin";
      router.push(
        `/${currentPortal}/venue/${venueId}/shop-items/${createdItem.id}`,
      );
    } else {
      // For edit operations, just refresh the list
      fetchShopItems();
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "food":
        return "bg-orange-100 text-orange-800";
      case "drink":
        return "bg-blue-100 text-blue-800";
      case "merchandise":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Shop items</CardTitle>
              <CardDescription>
                {loading
                  ? "Loading..."
                  : `${shopItems.length} item${shopItems.length !== 1 ? "s" : ""} available`}
              </CardDescription>
            </div>
            {canModify && (
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add new shop item
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-center">VAT %</TableHead>
                <TableHead>Accounting article</TableHead>
                <TableHead>Status</TableHead>
                {canModify && (
                  <TableHead className="text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shopItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canModify ? 8 : 7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No shop items found.{" "}
                    {canModify && "Click 'Add new shop item' to create one."}
                  </TableCell>
                </TableRow>
              ) : (
                shopItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.image_url ? (
                        <div className="w-16 h-12 relative rounded overflow-hidden border border-gray-200 hover:scale-105 transition-transform cursor-pointer bg-gray-50">
                          <Image
                            src={item.image_url}
                            alt={item.name}
                            fill
                            className="object-contain"
                            sizes="64px"
                            onError={(e) => {
                              // Handle broken images
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              target.parentElement!.innerHTML =
                                '<div class="w-full h-full bg-gray-100 rounded flex items-center justify-center"><span class="text-xs text-gray-400">Error</span></div>';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-16 h-12 bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
                          <span className="text-xs text-gray-400">
                            No image
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={getTypeColor(
                          item.category?.name || "unknown",
                        )}
                      >
                        {item.category?.name || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.pricing
                        ? `${item.pricing.price.toFixed(2)} ${getCurrencySymbol(venueCurrency)}`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {item.pricing ? `${item.pricing.vat_percentage}%` : "N/A"}
                    </TableCell>
                    <TableCell>
                      {item.article_code ? (
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-800 border-blue-200"
                        >
                          {item.article_code}
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="bg-amber-100 text-amber-800 border-amber-200"
                        >
                          Not mapped
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? "default" : "secondary"}>
                        {item.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    {canModify && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            title={`Edit ${item.name}`}
                            className="h-8 w-8 p-0 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit {item.name}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShopItemToDelete(item);
                              setShowDeleteDialog(true);
                            }}
                            title={`Delete ${item.name}`}
                            className="h-8 w-8 p-0 hover:bg-red-50 text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete {item.name}</span>
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {showFormDialog && (
        <ShopItemFormDialog
          open={showFormDialog}
          onOpenChange={setShowFormDialog}
          venueId={venueId}
          shopItem={selectedShopItem}
          onSuccess={handleFormSuccess}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete shop item"
        description="Are you sure you want to delete this shop item? Shop items that have been used in bookings cannot be deleted."
        itemName={shopItemToDelete?.name}
        itemSubtext={`Category: ${shopItemToDelete?.category?.name}`}
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </>
  );
}
